<script setup lang="ts">
  import { computed } from 'vue'
  import ContentImage from '../../atoms/ContentImage/ContentImage.vue'
  import ContentTitle from '../../atoms/ContentTitle/ContentTitle.vue'
  import ContentAttribution from '../../atoms/ContentAttribution/ContentAttribution.vue'
  import type { ContentCardProps } from './types'

  const props = withDefaults(defineProps<ContentCardProps>(), {
    variant: 'default',
    sourceName: '',
    sourceIconUrl: '',
    time: '',
    showPlayButton: false,
    inheritClass: '',
  })

  defineEmits<{
    click: []
  }>()

  const VARIANT_STYLES = {
    default: {
      container: 'relative overflow-hidden rounded-lg',
      titleColor: 'text-content-on-overlay',
      attributionClasses: 'text-content-on-overlay/80',
    },
    background: {
      container:
        'overflow-hidden rounded-lg border border-border-subtle shadow-article-card @container',
      titleColor: 'text-content-primary',
      attributionClasses: 'text-content-secondary',
    },
  } as const

  const styles = computed(() => VARIANT_STYLES[props.variant])
</script>

<template>
  <article
    :class="[styles.container, 'group h-full w-full cursor-pointer min-h-28']"
    role="link"
    tabindex="0"
    @click="$emit('click')"
    @keydown.enter="$emit('click')"
  >
    <template v-if="variant === 'default'">
      <!-- Full-bleed image -->
      <div class="absolute inset-0">
        <ContentImage
          :src="thumbnailUrl"
          :alt="title"
          :show-play-button="showPlayButton"
          darken-on-hover
        />
      </div>

      <!-- Gradient overlay -->
      <div
        class="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-overlay-strong to-transparent"
        data-testid="gradient-overlay"
      />

      <!-- Content pinned to bottom -->
      <div :class="['absolute inset-x-0 bottom-0 p-3', inheritClass]">
        <slot>
          <ContentAttribution
            v-if="sourceName || time || sourceIconUrl"
            :source-name="sourceName"
            :source-icon-url="sourceIconUrl"
            :time="time"
            :classes="styles.attributionClasses"
          />
          <ContentTitle
            :text="title"
            :classes="styles.titleColor"
            :max-lines="3"
            weight="font-semibold"
          />
        </slot>
      </div>
    </template>

    <template v-else>
      <!-- Vertical: image fills top, content at bottom -->
      <div class="hidden h-full flex-col bg-surface-primary @[250px]:flex">
        <div class="min-h-0 flex-1">
          <ContentImage
            :src="thumbnailUrl"
            :alt="title"
            :show-play-button="showPlayButton"
          />
        </div>
        <div :class="['shrink-0 p-3', inheritClass]">
          <slot>
            <ContentAttribution
              v-if="sourceName || time || sourceIconUrl"
              :source-name="sourceName"
              :source-icon-url="sourceIconUrl"
              :time="time"
              :classes="styles.attributionClasses"
            />
            <span class="group-hover:underline">
              <ContentTitle
                :text="title"
                :classes="styles.titleColor"
                weight="font-semibold"
                :max-lines="2"
              />
            </span>
          </slot>
        </div>
      </div>

      <!-- Horizontal: content left, small image right -->
      <div
        class="flex h-full items-center gap-2 bg-surface-primary p-2 @[250px]:hidden"
      >
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <slot>
            <span class="group-hover:underline">
              <ContentTitle
                :text="title"
                :classes="styles.titleColor"
                size="text-sm"
                weight="font-semibold"
                :max-lines="2"
              />
            </span>
            <ContentAttribution
              v-if="sourceName || time || sourceIconUrl"
              :source-name="sourceName"
              :source-icon-url="sourceIconUrl"
              :time="time"
              :classes="styles.attributionClasses"
            />
          </slot>
        </div>
        <div class="h-16 w-16 shrink-0">
          <ContentImage
            :src="thumbnailUrl"
            :alt="title"
            :show-play-button="showPlayButton"
            rounded-class="rounded-lg"
          />
        </div>
      </div>
    </template>
  </article>
</template>
